import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Youtube, Facebook, ChevronDown } from "lucide-react";
import { useLang, UI, SOCIALS } from "../i18n";
import { ContactForm } from "./ContactForm";
import { CourtLines, MonoTag } from "./motifs";
import { IMG, TRAVEL, PROJECTS, getProject } from "../content";

const NAV = [
  { to: "/", key: "home" },
  { to: "/programmes", key: "programmes", children: [
    { to: "/programmes/sky-hook", label: "Skyhook Prep", id: "sky-hook", color: "#A78BFA" },
    { to: "/programmes/parisian-prep", label: "Parisian Prep", id: "parisian-prep", color: "#2BD1E0" },
    { to: "/programmes/academy-of-basketball", label: "Academy of Basketball", id: "academy-of-basketball", color: "#FFB81C" },
    { to: "/programmes/european-team", label: "European Team", id: "european-team", color: "#F87171" },
  ] },
  { to: "/ligue", key: "ligue", children: [
    { to: "/ligue", label: "The European Circuit", id: "european-circuit", color: "#CFAB7A" },
  ] },
  { to: "/immersions", key: "immersions", children: [
    { to: "/immersions/paris", label: "Paris", id: "paris", color: "#38BDF8" },
    { to: "/immersions/montreal", label: "Montréal", id: "montreal", color: "#5B9BFF" },
    { to: "/immersions/toronto", label: "Toronto", id: "toronto", color: "#FF6B6B" },
    { to: "/immersions/carthagene", label: "Carthagène", id: "carthagene", color: "#FFB35C" },
  ] },
  { to: "/evenements", key: "evenements", children: [
    { to: "/evenements", label: "The Winter Cup", id: "winter-cup", color: "#A4C8E1" },
    { to: "/evenements/ligue-ete-montreal", label: "Ligue d'Été Montréal", id: "tryouts", color: "#1D4ED8" },
    { to: "/evenements/sejour-canada", label: "Séjour Canada", id: "sejour-canada", color: "#FF5C6C" },
    { to: "/evenements/sejour-ny-philly", label: "New York & Philly", id: "sejour-ny-philly", color: "#F58426" },
  ] },
  { to: "/sherbrooke", key: "equipes", children: [
    { to: "/sherbrooke", label: "Sherbrooke BC", id: "sherbrooke", color: "#FFC72C" },
    { to: "/legacy-3r", label: "Legacy 3R", id: "legacy-3r", color: "#FF8A3D" },
  ] },
  { to: "/alumni", key: "alumni" },
  { to: "/candidature", key: "candidature" },
  { to: "/offre", key: "offre" },
  { to: "/infos", key: "infos" },
  { to: "/boutique", key: "boutique" },
];

const NAV_LINKS = NAV.filter((n) => n.key !== "home");

const ICONS = { instagram: Instagram, youtube: Youtube, facebook: Facebook };

const SocialRow = ({ size = 20, className = "" }) => (
  <div className={"flex items-center gap-4 " + className}>
    {SOCIALS.map((s) => {
      const Icon = ICONS[s.icon];
      return (
        <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer"
          data-testid={`social-${s.icon}`} aria-label={s.name}
          className="text-neutral-400 hover:text-flame transition-colors duration-300">
          <Icon size={size} />
        </a>
      );
    })}
  </div>
);

const LangSwitcher = () => {
  const { lang, setLang, langs } = useLang();
  return (
    <div className="flex items-center gap-1 font-heading text-sm" data-testid="lang-switcher">
      {langs.map((l, i) => (
        <React.Fragment key={l}>
          {i > 0 && <span className="text-neutral-600">|</span>}
          <button data-testid={`lang-${l}`} onClick={() => setLang(l)}
            className={`px-1 uppercase transition-colors duration-200 ${lang === l ? "text-flame font-bold" : "text-neutral-400 hover:text-white"}`}>
            {l}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};

const Header = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  // Mini-site aware header logo: swap ASV logo for the active project's logo
  const active = useMemo(() =>
    PROJECTS.find((p) => p.logo && pathname === `/${p.category}/${p.slug}`) ||
    (pathname === "/ligue" && getProject("european-circuit")) ||
    (pathname === "/evenements" && getProject("winter-cup")) ||
    (pathname === "/sherbrooke" && { logo: IMG.logoSBC }) ||
    (pathname === "/legacy-3r" && { logo: IMG.logoLegacy }) ||
    null, [pathname]);
  const headerLogo = (active && active.logo) || IMG.logoASV;
  const wcTheme = getProject("winter-cup").theme;
  const tickerMsg = t({
    fr: "THE WINTER CUP · 26 → 30 DÉC · NOUVEAU LIEU : ATHLETICA, EAUBONNE (95) · 3 TERRAINS · EARLY BIRD DÈS 190 € · JE M'INSCRIS",
    en: "THE WINTER CUP · DEC 26 → 30 · NEW VENUE: ATHLETICA, EAUBONNE (95) · 3 COURTS · EARLY BIRD FROM €190 · REGISTER NOW",
    es: "THE WINTER CUP · 26 → 30 DIC · NUEVO LUGAR: ATHLETICA, EAUBONNE (95) · 3 CANCHAS · EARLY BIRD DESDE 190 € · INSCRÍBETE",
  });
  return (
    <header className="fixed top-0 inset-x-0 z-[60] bg-black/50 backdrop-blur-xl border-b border-white/10">
      <Link to="/evenements" data-testid="winter-cup-ticker" aria-label="The Winter Cup"
        className="block overflow-hidden h-7 text-black" style={{ backgroundColor: wcTheme.accent }}>
        <div className="ticker-track flex w-max items-center h-7">
          {[0, 1].map((k) => (
            <span key={k} className="flex items-center whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.2em] pr-10">
              ★ {tickerMsg} ★ {tickerMsg}
            </span>
          ))}
        </div>
      </Link>
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-3 shrink-0">
          <img src={headerLogo} alt="Airsports Travel" data-testid="header-logo"
            className="h-10 w-10 md:h-12 md:w-12 rounded-full object-contain bg-white p-0.5 shadow-lg" />
          <span className="font-display text-xl md:text-2xl tracking-tight leading-none">AIR<span className="text-flame">SPORTS</span></span>
        </Link>
        <nav className="hidden xl:flex items-center gap-3.5 font-heading text-[11px] 2xl:text-xs">
          {NAV_LINKS.map((n) => (
            <div key={n.key} className="relative group">
              <NavLink to={n.to} data-testid={`nav-${n.key}`}
                className={({ isActive }) => `inline-flex items-center gap-1 uppercase tracking-wide transition-colors duration-200 ${isActive ? "text-flame" : "text-neutral-300 hover:text-white"}`}>
                {t(UI.nav[n.key])}
                {n.children && <ChevronDown size={13} className="opacity-60 group-hover:rotate-180 transition-transform duration-200" />}
              </NavLink>
              {n.children && (
                <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 hidden group-hover:block group-focus-within:block z-50">
                  <div className="relative z-50 min-w-[260px] bg-[#070C16] border border-white/15 rounded-md py-2 shadow-xl shadow-black/60">
                    {n.children.map((c, ci) => (
                      <NavLink key={c.id} to={c.to} data-testid={`subnav-${c.id}`}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${c.color}22`; e.currentTarget.style.color = c.color; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.color = ""; }}
                        className={`flex items-center gap-3 px-5 py-3 font-heading text-sm uppercase tracking-wide text-neutral-200 transition-colors duration-150 ${ci > 0 ? "border-t border-white/[0.07]" : ""}`}>
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: c.color, boxShadow: `0 0 8px ${c.color}88` }} />
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-5">
          <LangSwitcher />
        </div>
        <button data-testid="mobile-menu-btn" className="xl:hidden text-white" onClick={() => setOpen(true)} aria-label="Menu">
          <Menu size={26} />
        </button>
      </div>
      {scrolled && <div className="h-[2px] w-full bg-gradient-to-r from-flame via-azure to-gold" />}

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#070C16] xl:hidden flex flex-col">
            <div className="flex items-center justify-between px-5 h-16 pt-2 shrink-0">
              <span className="font-display text-xl">AIR<span className="text-flame">SPORTS</span></span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close"><X size={26} /></button>
            </div>
            <nav data-lenis-prevent className="flex flex-col px-5 pt-6 gap-1 flex-1 overflow-y-auto overscroll-contain pb-8">
              {NAV.map((n, i) => (
                <motion.div key={n.key} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <NavLink to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.key}`}
                    className={({ isActive }) => `block font-display text-2xl py-1.5 uppercase ${isActive ? "text-flame" : "text-white"}`}>
                    {t(UI.nav[n.key])}
                  </NavLink>
                  {n.children && (
                    <div className="pl-4 border-l border-white/10 mb-2">
                      {n.children.map((c) => (
                        <NavLink key={c.id} to={c.to} onClick={() => setOpen(false)} data-testid={`msubnav-${c.id}`}
                          className="flex items-center gap-2.5 font-heading text-sm uppercase tracking-wide text-neutral-400 py-1.5 hover:text-white transition-colors duration-150">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </nav>
            <div className="px-5 py-5 shrink-0 border-t border-white/10 flex items-center justify-between">
              <LangSwitcher />
              <SocialRow />
            </div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
};

export const ContactSection = ({ defaultProgram, accent, programs, trips }) => {
  const { t } = useLang();
  const ac = accent || "var(--ms-accent, #E5352B)";
  return (
    <section id="contact" className="relative border-t border-white/10 py-24 md:py-32 overflow-hidden grain"
      style={{ backgroundColor: "var(--ms-surface, #101A30)" }}>
      <CourtLines className="-right-56 -top-56 w-[640px] h-[640px] hidden lg:block" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <MonoTag className="text-white/60">{t(TRAVEL.gate)} 01 · {t(UI.form.title)}</MonoTag>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.9] mt-4">
            {t(UI.cta.join)}
          </h2>
          <div className="flex items-center gap-3 mt-6 font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: ac }}>
            <span>{t(TRAVEL.nowBoarding)}</span><span className="blink">●</span>
          </div>
          <p className="font-body text-white/60 mt-5 max-w-md">{t(UI.form.subtitle)}</p>
          <a href="mailto:airsportstravel@gmail.com" data-testid="contact-email-link"
            className="inline-block mt-6 font-heading text-lg hover:opacity-70 transition-opacity duration-300">
            airsportstravel@gmail.com
          </a>
          <div className="mt-8"><SocialRow size={22} /></div>
        </div>
        <div className="lg:col-span-7">
          <ContactForm defaultProgram={defaultProgram} accent={accent} programs={programs} trips={trips} />
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-black border-t border-white/10">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <img src={IMG.logoASV} alt="Air Sports Travel" className="h-14 w-14 rounded-full object-contain bg-white p-1 shadow-lg" />
            <div className="font-display text-2xl md:text-3xl leading-none">AIR<span className="text-flame">SPORTS</span>TRAVEL</div>
          </div>
          <p className="font-body text-neutral-500 mt-3 text-sm">{t(UI.footer.tagline)}</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-600 mt-2">PAR · YUL · YYZ · CTG · BCN · MIL</p>
          <div className="mt-5"><SocialRow /></div>
        </div>
        <div>
          <p className="font-heading uppercase text-xs tracking-[0.2em] text-neutral-500">{t(UI.footer.nav)}</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className="font-body text-sm text-neutral-400 hover:text-flame transition-colors duration-200">
                {t(UI.nav[n.key])}
              </NavLink>
            ))}
          </div>
        </div>
        <div>
          <p className="font-heading uppercase text-xs tracking-[0.2em] text-neutral-500">{t({ fr: "Adhésion & Politiques", en: "Membership & Policies", es: "Adhesión y Políticas" })}</p>
          <ul className="mt-4 space-y-2 font-body text-sm text-neutral-400">
            <li data-testid="footer-membership">{t({ fr: "Adhésion annuelle : 200 €", en: "Annual membership: €200", es: "Adhesión anual: 200 €" })}</li>
            <li>{t({ fr: "Non-remboursement · avoirs valables 2 ans", en: "No refund · credit notes valid 2 years", es: "No reembolso · abonos válidos 2 años" })}</li>
            <li>{t({ fr: "Sécurité des mineurs & encadrement certifié", en: "Minor safety & certified supervision", es: "Seguridad de menores y supervisión certificada" })}</li>
            <li>{t({ fr: "Mentions légales & gestion des risques", en: "Legal notice & risk management", es: "Aviso legal y gestión de riesgos" })}</li>
          </ul>
          <NavLink to="/infos" data-testid="footer-infos-link"
            className="inline-block mt-3 font-heading text-sm text-flame hover:opacity-80 transition-opacity duration-200">
            {t(UI.nav.infos)} →
          </NavLink>
        </div>
        <div>
          <p className="font-heading uppercase text-xs tracking-[0.2em] text-neutral-500">{t(UI.footer.follow)}</p>
          <a href="mailto:airsportstravel@gmail.com" className="block mt-4 font-heading text-lg hover:text-flame transition-colors duration-300">
            airsportstravel@gmail.com
          </a>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center font-body text-xs text-neutral-600">
        © {new Date().getFullYear()} Air Sports Travel. {t(UI.footer.rights)}
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="min-h-screen bg-ink text-white">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
};

export { SocialRow };
